import React, { useEffect, useState } from 'react';
import { Typography } from '@material-ui/core';
import { Box, Grid, Tab, Tabs, styled } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import Sales from './Sales';
import Summary from './Summary';
import InventorySummary from './InventorySummary';
import Location from './Location';
import Replenishment from './Replenishment';
import {
	getTopBottomSalesGrid,
	getMapConfig,
} from '../../redux/actions/itemDrillDownActions';
import Filter from '../Filter/Filter';
import { isSaturday } from '../../helper/date';
import { UserPortalContext } from '../../context/appcontext';
import { makeStyles } from '@material-ui/core/styles';
import { BOGLE_BOLD } from '../../constants/constant';
import { colors } from '../../theme/color-palette';
import { Breadcrumb } from '../Breadcrumb';
import { useParams } from 'react-router-dom';
import CatmanView from '../Filter/CatmanView';
import { saveEventTrack } from '../../redux/actions/eventTrackAction';

const styles = makeStyles((theme) => ({
	title: {
		float: 'left',
		fontFamily: 'Bogle-Regular !important',
		fontWeight: 'normal',
		color: theme.palette?.text?.main,
		fontSize: 24,
		marginLeft: 4,
		lineHeight: '64px',
		wordBreak: 'keep-all',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		[theme.breakpoints?.down('md')]: {
			fontSize: 20,
			marginLeft: 0,
		},
	},

	headerInsights: {
		width: '100%',
		fontSize: '24px',
		fontWeight: 700,
		borderBottom: `1px solid ${colors.LIST_BORDER_CLR}`,
		alignItems: 'center',
		display: 'column',
		padding: '24px 20px 0 20px',
		gap: '16px',
	},

	itemNameHeading: {
		marginTop: '12px 0px',
		padding: '2px 4px',
		fontSize: '24px',
		lineHeight: '36px',
		fontFamily: BOGLE_BOLD,
		letterSpacing: 'normal',
		textTransform: 'capitalize',
	},
	breadcrumbs: {
		padding: '0px 4px',
	},
	breadcrumb: {
		fontSize: '14px',
		color: colors.BREADCRUMB_INACTIVE,
		textDecoration: 'none',
		'&:hover': {
			cursor: 'pointer',
		},
	},

	breadcrumbActive: {
		fontSize: '14px',
		color: colors.BREADCRUMB_ACTIVE,
	},

	pageHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: '16px',
	},
}));

function DrilldownItem() {
	const classes = styles();
	const [value, setValue] = useState(0);
	const portalUserContext = React.useContext(UserPortalContext);
	const dispatch = useDispatch();

	// Route params
	let {
		itemName, // Item Name
		itemId, // Item Number
	} = useParams();

	itemName = itemName?.replace('%2F', '/');

	const tabs = [
		{ label: 'Summary', value: 'Summary' },
		{ label: 'Sales', value: 'Sales' },
		{ label: 'Inventory', value: 'Inventory' },
		{ label: 'Location', value: 'Location' },
		{ label: 'OPD', value: 'OPD' }, // changed from Replenishment to OPD
	];

	const selectedFilters = useSelector(
		(state) => state.salesReducer.selectedFilters
	);

	const isCatmanUser = useSelector((state) => state.appGuideReducer.isCatman);

	const sessionWeekFilters = JSON.parse(sessionStorage.getItem('weekfilters'));

	const {
		weekFilter: timePeriod,
		startWeek,
		endWeek,
		startDate,
		endDate,
	} = sessionWeekFilters && Object.keys(sessionWeekFilters)?.length != 0
		? sessionWeekFilters
		: selectedFilters;

	const [weekFilter, setWeekFilter] = useState(
		isSaturday ? 1 : timePeriod || 0
	);

	const { selectedFilter } = selectedFilters;

	useEffect(() => {
		dispatch(getMapConfig({ portalUserContext }));
		dispatch(getTopBottomSalesGrid('asc'));
	}, []);

	const handleChange = (e, value) => {
		setValue(value);

		const tabName = tabs[value]?.label;

		// track the tab on drilldown item
		const request = {
			event: 'Tab Change',
			event_description: `Selected Tab:  ${tabName}`,
			attributes: 'Item Details',
		};
		dispatch(saveEventTrack({ request, portalUserContext }));
	};

	const StyledTabs = styled((props) => (
		<Tabs
			{...props}
			TabIndicatorProps={{
				children: <span className="MuiTabs-indicatorSpan" />,
			}}
		/>
	))({
		'& .MuiTabs-indicator': {
			borderRadius: '3px 3px 0px 0px',
			display: 'flex',
			justifyContent: 'center',
			backgroundColor: '#6245B7',
			height: '3px',
		},
		'& .MuiTabs-indicatorSpan': {
			maxWidth: 10,
			width: '10%',
			backgroundColor: '#635ee7',
		},
	});

	const StyledTab = styled((props) => <Tab disableRipple {...props} />)(() => ({
		minWidth: 0,
		textTransform: 'none',
		fontFamily: 'Bogle',
		fontSize: '14px',
		color: 'black',
		'&.Mui-selected': {
			color: 'black',
			fontWeight: '700',
		},
		'&.MuiButtonBase-root': {
			padding: '0px 12px',
		},
		'&.Mui-focusVisible': {
			backgroundColor: 'black',
		},
	}));

	const tabName = tabs[value]?.label;

	return (
		<Box>
			<Grid container>
				<Box className={classes.headerInsights}>
					<Box className={classes.pageHeader}>
						<Breadcrumb />
						<Box style={{ marginRight: '50px', marginTop: '5px' }}>
							{isCatmanUser && <CatmanView isItemDrilldown={true} />}
						</Box>
					</Box>
					<Box>
						<Typography className={classes.itemNameHeading}>
							Item:{' '}
							{itemName
								? `${itemId} ${String(itemName)?.toLocaleLowerCase()}`
								: itemId}
						</Typography>
					</Box>
					<StyledTabs
						value={value}
						onChange={handleChange}
						data-testid="insights-item-details-tabs"
						aria-label="dashboard-insights">
						{tabs?.map((tab) => (
							<StyledTab key={tab?.value} label={tab?.label} />
						))}
					</StyledTabs>
				</Box>

				<Box style={{ margin: '22px 38px 11px 24px', width: '100%' }}>
					<Filter
						weekFilter={weekFilter}
						setWeekFilter={setWeekFilter}
						selectedFilter={selectedFilter}
						startWeek={startWeek}
						endWeek={endWeek}
						startDate={startDate}
						endDate={endDate}
						isItemDrilldown={true}
					/>
				</Box>
				{/* </Box> */}

				<Box sx={{ width: '100%', margin: '0px 30px' }}>
					<Grid item>
						{tabName === 'Sales' && (
							<Sales
								weekFilter={weekFilter}
								selectedFilter={selectedFilter}
								selectedTab={tabName}
								tab={tabName}
								startDate={startDate}
								endDate={endDate}
								itemNum={itemId}
							/>
						)}
						{tabName === 'Summary' && (
							<Summary
								weekFilter={weekFilter}
								selectedFilter={selectedFilter}
								selectedTab={tabName}
								tab={tabName}
								startDate={startDate}
								endDate={endDate}
								itemNum={itemId}
								itemName={itemName}
							/>
						)}
						{tabName === 'Inventory' && (
							<InventorySummary
								weekFilter={weekFilter}
								selectedFilter={selectedFilter}
								selectedTab={tabName}
								tab={tabName}
								startDate={startDate}
								endDate={endDate}
								itemNum={itemId}
								itemName={itemName}
							/>
						)}
						{tabName === 'Location' && (
							<Location
								weekFilter={weekFilter}
								selectedTab={tabName}
								tab={tabName}
								selectedFilter={selectedFilter}
								startDate={startDate}
								endDate={endDate}
								itemNum={itemId}
							/>
						)}
						{tabName === 'OPD' && ( // Replenishment
							<Replenishment
								selectedTab={tabName}
								weekFilter={weekFilter}
								selectedFilter={selectedFilter}
								startDate={startDate}
								endDate={endDate}
								itemNum={itemId}
								itemName={itemName}
							/>
						)}
					</Grid>
				</Box>
			</Grid>
		</Box>
	);
}

export default DrilldownItem;
